import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Award, ExternalLink, XCircle, Loader2 } from 'lucide-react';

/**
 * Public certificate verification page.
 * Hit at /verify/SCC-XXXXXXXX from anywhere — LinkedIn, resume, email — without login.
 * Returns a branded proof-of-completion display: worker's first name + last-initial,
 * module title, score, completion date, plus a bold "Verified by ScensobConnect ✓" seal.
 * Never leaks worker email/phone/employer.
 */
export default function VerifyCertificatePage() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backend}/api/verify/${encodeURIComponent(certId || '')}`);
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'Certificate not found or has been revoked.' : 'Verification service temporarily unavailable.');
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, data: null, error: err.message || 'Unable to verify certificate.' });
      }
    })();
    return () => { cancelled = true; };
  }, [certId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-6" data-testid="verify-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin mx-auto mb-3" />
          <p className="text-slate-600">Verifying certificate {certId}…</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.data?.verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center" data-testid="verify-not-found">
          <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Certificate Not Verified</h1>
          <p className="text-slate-600 mb-4">{state.error || 'This certificate ID is not on record.'}</p>
          <p className="text-xs text-slate-500 mb-6">Certificate IDs follow the pattern <code className="bg-slate-100 px-1.5 py-0.5 rounded">SCC-XXXXXXXX</code>.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition" data-testid="verify-home-link">
            Go to ScensobConnect →
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center p-6" data-testid="verify-success">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header band */}
          <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-700 p-6 text-white text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 mb-3">
              <ShieldCheck className="w-4 h-4" />
              <span className="text-sm font-semibold tracking-wide uppercase">Verified by ScensobConnect</span>
            </div>
            <h1 className="text-3xl font-bold">Certificate of Completion</h1>
            <p className="text-white/80 text-sm mt-1">Publicly verified on {new Date().toLocaleDateString(undefined, { dateStyle: 'medium' })}</p>
          </div>

          {/* Body */}
          <div className="p-8 space-y-6">
            <div className="text-center">
              <p className="text-sm uppercase tracking-wide text-slate-500 mb-1">This certifies that</p>
              <p className="text-4xl font-bold text-slate-900" data-testid="verify-worker-name">{d.worker_display_name}</p>
              <div className="w-24 h-0.5 bg-teal-500 mx-auto my-4"></div>
              <p className="text-sm uppercase tracking-wide text-slate-500 mb-1">has successfully completed</p>
              <p className="text-2xl font-semibold text-teal-700" data-testid="verify-module-title">“{d.module_title}”</p>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-100">
              <div className="text-center">
                <p className="text-xs text-slate-500 uppercase mb-1">Score</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="verify-score">{d.best_score}%</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500 uppercase mb-1">Completed</p>
                <p className="text-sm font-semibold text-slate-800 mt-1" data-testid="verify-completed">{d.completed_display}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500 uppercase mb-1">Issuer</p>
                <p className="text-sm font-semibold text-slate-800 mt-1">{d.issuer}</p>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div className="text-sm text-emerald-800">
                <p className="font-semibold">This certificate is valid and on record.</p>
                <p className="text-xs mt-0.5">Certificate ID: <code className="bg-white px-1.5 py-0.5 rounded">{d.certificate_id}</code></p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-500">
                <Award className="w-4 h-4" />
                <span className="text-xs">ScensobConnect Staff Development</span>
              </div>
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700" data-testid="verify-visit-link">
                Visit ScensobConnect
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">
          Anyone can independently verify this certificate at scensobconnect.com/verify/{d.certificate_id}
        </p>
      </div>
    </div>
  );
}
